import { FormPercentageInput } from '@exam-management/components';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus, QConfigStructureCategory } from '@exam-management/graphql';
import {
  ErrorOutlineOutlinedIcon,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Text,
} from '@shared/components';
import { TableHeader, type TableHeaderRow } from '@shared/table-generator';
import { groupBy } from '@shared/utils';
import { useMemo } from 'react';

import { useQConfigSettingsFormContext } from '../../providers';
import { calculateScore } from '../../utils';

interface PartScoreTableProps {
  disabled: boolean;
}

const headers: TableHeaderRow[] = [
  {
    children: [
      {
        id: 'part-criteria-name',
        children: 'หมวดที่บังคับผ่าน',
        align: 'center',
        rowSpan: 1,
        sx: {
          flex: 1,
        },
      },
      {
        id: 'part-score-criteria',
        children: 'คะแนนทั้งหมด',
        align: 'center',
        rowSpan: 1,
        sx: {
          width: '140px',
        },
      },
      {
        id: 'part-score-percentage-input',
        children: 'เปอร์เซ็นต์',
        align: 'center',
        rowSpan: 1,
        sx: {
          width: '120px',
        },
      },
      {
        id: 'calculated-part-score-criteria',
        children: 'คะแนน',
        align: 'center',
        rowSpan: 1,
        sx: {
          width: '120px',
        },
      },
    ],
  },
];

export default function PartScoreTable(props: Readonly<PartScoreTableProps>) {
  const { disabled: isDisabled } = props;
  const { qConfigData } = useQConfig();
  const { structures, status } = qConfigData;

  const formContext = useQConfigSettingsFormContext();
  const { formState, watch } = formContext;
  const { errors, isSubmitted } = formState;
  const structurePassingCriteria = useMemo(() => {
    const formStructurePassingCriteria = watch('passingCriteria.structures') ?? {};
    if (status === QConfigStatus.Draft) {
      return formStructurePassingCriteria;
    }
    return structures.reduce<Record<string, number | null>>((acc, structure) => {
      const { id, passingCriteria } = structure;
      return {
        ...acc,
        [id]: passingCriteria?.value ?? null,
      };
    }, {});
  }, [structures, status, watch]);

  const structureCriteriaErrors = errors.passingCriteria?.structures;
  const errorMessage = structureCriteriaErrors?.root?.message;

  const rows = useMemo(() => {
    const allCourses = structures
      .filter((structure) => structure.category === QConfigStructureCategory.Course)
      .sort((a, b) => {
        const aQBS = structures.find((item) => item.id === a.parentId);
        const bQBS = structures.find((item) => item.id === b.parentId);

        return (aQBS?.order ?? 0) - (bQBS?.order ?? 0);
      });

    const allParts = structures
      .filter((structure) => structure.category === QConfigStructureCategory.Part)
      .sort((a, b) => {
        return (a.order ?? 0) - (b.order ?? 0);
      });

    const allPartsByParentId = groupBy(allParts, 'parentId');

    const rows: (typeof structures)[number][] = [];

    for (const course of allCourses) {
      const parts = allPartsByParentId?.[course.id] ?? [];
      rows.push(...parts);
    }

    return rows;
  }, [structures]);

  const handleChange = () => {
    if (isSubmitted) {
      formContext.trigger(['passingCriteria.structures']);
    }
  };

  return (
    <>
      <TableContainer
        sx={{
          borderRadius: (theme) => theme.shape.rounded(2),
          border: '1px solid',
          borderColor: (theme) => theme.palette.grey[300],
        }}
      >
        <Table>
          <TableHeader
            columns={headers}
            slotProps={{
              tableHeaderRow: {
                sx: (theme) => ({
                  '& > :not(th:first-of-type)': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                  },
                }),
              },
              tableHeaderCell: {
                sx: (theme) => ({
                  ...theme.typography.body1Semibold,
                  backgroundColor: theme.palette.grey[100],
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                  px: theme.spacing(3),
                  py: theme.spacing(2),
                }),
              },
            }}
          />
          <TableBody
            sx={{
              '& > :not(tr:last-of-type) td': {
                borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
              },
            }}
          >
            {rows.map((row) => {
              const rowPercentage = structurePassingCriteria[row.id] ?? 0;
              const calculatedScore = calculateScore(row.score, rowPercentage);

              return (
                <TableRow
                  key={row.id}
                  sx={(theme) => ({
                    '& > :not(td:first-of-type)': {
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                    },
                    '& > :not(td:last-of-type)': {
                      borderBottom: 0,
                    },
                    '& > td': {
                      p: theme.spacing(2),
                    },
                  })}
                >
                  <TableCell>
                    <Text variant='body2Semibold'>{row.name}</Text>
                  </TableCell>
                  <TableCell align='center'>
                    <Text variant='body1Semibold'>{row.score}</Text>
                  </TableCell>
                  <TableCell align='center'>
                    <FormPercentageInput
                      name={`passingCriteria.structures.${row.id}`}
                      onChange={handleChange}
                      disabled={status !== QConfigStatus.Draft || isDisabled}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Text variant='body1Semibold'>{calculatedScore}</Text>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {errorMessage ? (
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(1),
            alignItems: 'center',
            color: (theme) => theme.palette.error.main,
          })}
        >
          <ErrorOutlineOutlinedIcon sx={{ fontSize: '24px' }} />
          <Text variant='body2'>{errorMessage}</Text>
        </Flex>
      ) : null}
    </>
  );
}
